import type { UploadFile } from 'antd';
import { BASE_REST_API_URL } from 'common/constants';
import type { Hardware, InventoryMeter, SerializedHardware, SerializedHardwarePackage } from '@enmon/commons/lib/tools';
import { buildQuery } from '@enmon/commons/lib/tools/utils/buildQuery';
import { stringify } from 'qs';
import type { BaseAPIParams } from 'lib/utils/BaseAPIParams';
import { BaseApiClient } from './baseApiClient';

export type HardwareDto = Partial<SerializedHardware>;
export type CreateHardwareArg = HardwareDto;
export interface UpdateHardwareArgs {
  id: Hardware['id'];
  values: HardwareDto;
}
export type DeleteHardwareArg = Hardware['id'];
export type HardwaresResult = SerializedHardware[];

export type HardwaresFilter = BaseAPIParams<SerializedHardware> & {
  inventoryMeter?: InventoryMeter['id'];
  hardwares?: ReadonlyArray<SerializedHardware['id']>;
  hardwarePackage?: SerializedHardwarePackage['id'];
};

export class HardwaresApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('hardwares', BASE_REST_API_URL, jwt);
  }

  public async find(filter: HardwaresFilter, signal: AbortSignal | undefined): Promise<HardwaresResult> {
    const { inventoryMeter, hardwares, hardwarePackage, fields, page, pageSize, limit, start } = filter;
    const response = await this.axios.get<HardwaresResult>(``, {
      params: `${buildQuery<SerializedHardware>({
        filters: {
          hardware_packages: {
            id: { $eq: hardwarePackage },
          },
        },
        fields,
        pagination: {
          page,
          pageSize,
          start,
          // NOTE: limit -1 will return all items from the API
          limit,
        },
        // NOTE: BE is implementing some custom filtering outside of the supported strapi query
      })}&${stringify({ inventoryMeter, hardwares })}`,
      signal,
    });
    return response.data;
  }

  public async create(values: CreateHardwareArg): Promise<SerializedHardware> {
    const response = await this.axios.post<SerializedHardware>('', { data: values });

    return response.data;
  }

  public async update({ id, values }: UpdateHardwareArgs): Promise<SerializedHardware> {
    const response = await this.axios.put<SerializedHardware>(`${id}`, { data: values });

    return response.data;
  }

  public async delete(id: DeleteHardwareArg): Promise<SerializedHardware> {
    const response = await this.axios.delete<SerializedHardware>(`${id}`);

    return response.data;
  }

  public async uploadCsv(file: UploadFile): Promise<void> {
    const formData = new FormData();

    formData.append('files.file', file.originFileObj);

    // data is required to be in the body of the multipart request
    const data = JSON.stringify({});
    formData.append('data', data);

    await this.axios.post<void>(`/upload/csv`, formData);
  }
}
